import { useNavigate, useSearch } from "@tanstack/react-router";
import { CheckIcon, PlusIcon, TicketIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { type Decision, DecideDialog } from "@/approvals/DecideDialog";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { StatusPill, statusLabel, type StatusTone } from "@/components/status-pill";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/dialog";
import { Input, Textarea } from "@/components/ui/input";
import { Label, Skeleton } from "@/components/ui/primitives";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNotifications } from "@/notifications/api";
import { formatDateValue } from "@/entities/format";
import { type TicketState, usePortalSolutions, usePortalTicket, usePortalTickets, useRaiseTicket } from "./api";

const TONE: Record<string, StatusTone> = {
  approval_requested: "warning",
  approved: "info",
  in_progress: "info",
  awaiting_sign_off: "warning",
  pending_info: "warning",
  hold: "neutral",
  completed: "success",
  cancelled: "neutral",
  rejected: "danger",
};

const PRIORITIES = [
  { value: "p1", label: "P1 – Critical" },
  { value: "p2", label: "P2 – High" },
  { value: "p3", label: "P3 – Medium" },
  { value: "p4", label: "P4 – Low" },
];

const day = (v: string | null | undefined) => formatDateValue(v) || "—";

/** Portal → Tickets: raise and follow tickets; approvers decide pending ones here too. URL: `state`, `ticket`. */
export function PortalTicketsPage() {
  const search = useSearch({ strict: false }) as { state?: string; ticket?: string };
  const navigate = useNavigate();
  const state: TicketState = search.state === "closed" ? "closed" : search.state === "all" ? "all" : "open";
  const tickets = usePortalTickets(state);
  const [raising, setRaising] = useState(false);
  const setSearch = (patch: Record<string, string | undefined>) =>
    void navigate({ to: "/portal/$section", params: { section: "tickets" }, search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }) });

  return (
    <>
      <PageHeader
        title="Tickets"
        description="Raise a support ticket and follow its progress."
        actions={
          <Button size="sm" onClick={() => setRaising(true)}>
            <PlusIcon /> Raise a ticket
          </Button>
        }
      />
      <Tabs value={state} onValueChange={(v) => setSearch({ state: v === "open" ? undefined : v })} className="mb-3">
        <TabsList aria-label="Which tickets">
          <TabsTrigger value="open">Open</TabsTrigger>
          <TabsTrigger value="closed">Closed</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
      </Tabs>
      {tickets.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : tickets.isError ? (
        <p className="text-sm text-muted-foreground">Couldn't load your tickets. Try again shortly.</p>
      ) : !tickets.data.length ? (
        <EmptyState icon={TicketIcon} title={state === "open" ? "No open tickets" : "No tickets"} description="Tickets you and your colleagues raise appear here." />
      ) : (
        <ul className="divide-y rounded-lg border bg-card" aria-label="Tickets">
          {tickets.data.map((t) => (
            <li key={t.id}>
              <button type="button" className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left hover:bg-muted/50" onClick={() => setSearch({ ticket: t.id })}>
                <span className="w-20 shrink-0 font-mono text-xs text-muted-foreground">{t.number}</span>
                <span className="min-w-0 flex-1">
                  <span className="block font-medium break-words">{t.title}</span>
                  <span className="block text-xs text-muted-foreground">
                    {[t.bau_solution, t.requested_by && `requested by ${t.requested_by}`, `raised ${day(t.requested_at)}`].filter(Boolean).join(" · ")}
                  </span>
                </span>
                <StatusPill tone={TONE[t.status] ?? "neutral"}>{statusLabel(t.status)}</StatusPill>
              </button>
            </li>
          ))}
        </ul>
      )}
      {raising && <RaiseTicketDialog onClose={() => setRaising(false)} onRaised={(id) => setSearch({ ticket: id, state: undefined })} />}
      <TicketSheet id={search.ticket} onClose={() => setSearch({ ticket: undefined })} />
    </>
  );
}

function RaiseTicketDialog({ onClose, onRaised }: { onClose: () => void; onRaised: (id: string) => void }) {
  const solutions = usePortalSolutions();
  const raise = useRaiseTicket();
  const [form, setForm] = useState({ title: "", detailed_requirement: "", bau_solution_id: "", priority: "p3" });
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const ready = form.title.trim().length >= 3 && form.detailed_requirement.trim().length >= 3 && form.bau_solution_id;
  const submit = () =>
    raise.mutate(form, {
      onSuccess: (t) => {
        toast.success(`Ticket ${t.number} raised`, { description: "We'll email you as it progresses." });
        onClose();
        onRaised(t.id);
      },
      onError: (e) => toast.error("Couldn't raise the ticket", { description: e instanceof Error ? e.message : undefined }),
    });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Raise a ticket</DialogTitle>
          <DialogDescription>Tell us what you need. Tickets outside your support subscription go to your approver first.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pt-solution">Solution</Label>
            <Select value={form.bau_solution_id || undefined} onValueChange={(v) => set("bau_solution_id", v)}>
              <SelectTrigger id="pt-solution" aria-label="Solution">
                <SelectValue placeholder={solutions.isPending ? "Loading…" : "Choose the solution this is about"} />
              </SelectTrigger>
              <SelectContent>
                {(solutions.data ?? []).map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                    {s.department ? ` (${s.department})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pt-title">Title</Label>
            <Input id="pt-title" maxLength={100} value={form.title} onChange={(e) => set("title", e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pt-priority">Priority</Label>
            <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger id="pt-priority" aria-label="Priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pt-detail">What do you need?</Label>
            <Textarea id="pt-detail" rows={5} value={form.detailed_requirement} onChange={(e) => set("detailed_requirement", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!ready || raise.isPending} onClick={submit}>
            {raise.isPending ? "Raising…" : "Raise ticket"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TicketSheet({ id, onClose }: { id: string | undefined; onClose: () => void }) {
  const ticket = usePortalTicket(id);
  const pane = useNotifications();
  const todo = pane.data?.todos.find((t) => t.entity === "ticket" && t.entity_id === id);
  const [deciding, setDeciding] = useState<Decision | null>(null);
  const t = ticket.data;
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{t ? `${t.number}: ${t.title}` : "Ticket"}</SheetTitle>
          <SheetDescription>{t ? [t.bau_solution, `raised ${day(t.requested_at)}`].filter(Boolean).join(" · ") : "Loading…"}</SheetDescription>
        </SheetHeader>
        <SheetBody className="flex flex-col gap-4 py-4 text-sm">
          {ticket.isError && <p className="text-muted-foreground">This ticket isn't available.</p>}
          {t && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <StatusPill tone={TONE[t.status] ?? "neutral"}>{statusLabel(t.status)}</StatusPill>
                <span className="text-muted-foreground">{t.priority.toUpperCase()}</span>
              </div>
              {todo && (
                <div className="rounded-lg border border-warning/40 bg-warning/10 p-3">
                  <p className="font-medium">This ticket is waiting for your approval.</p>
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" onClick={() => setDeciding({ id: todo.id, title: todo.title, requestedBy: todo.requested_by, decision: "approve" })}>
                      <CheckIcon /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setDeciding({ id: todo.id, title: todo.title, requestedBy: todo.requested_by, decision: "reject" })}>
                      <XIcon /> Reject
                    </Button>
                  </div>
                </div>
              )}
              <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1.5">
                <dt className="text-muted-foreground">Requested by</dt>
                <dd>{t.requested_by ?? "—"}</dd>
                <dt className="text-muted-foreground">Assigned to</dt>
                <dd>{t.assigned_to ?? "Not yet assigned"}</dd>
                <dt className="text-muted-foreground">Completed</dt>
                <dd>{day(t.completed_at)}</dd>
              </dl>
              <div>
                <h3 className="mb-1 text-xs font-semibold text-muted-foreground uppercase">Requirement</h3>
                <p className="whitespace-pre-wrap">{t.detailed_requirement}</p>
              </div>
              {t.solution_provided && (
                <div>
                  <h3 className="mb-1 text-xs font-semibold text-muted-foreground uppercase">Solution</h3>
                  <p className="whitespace-pre-wrap">{t.solution_provided}</p>
                </div>
              )}
            </>
          )}
        </SheetBody>
      </SheetContent>
      {deciding && <DecideDialog decision={deciding} onClose={() => setDeciding(null)} />}
    </Sheet>
  );
}
